import { useEffect, useRef, useState } from "react";
import {
  cancelPendingGenerationExit,
  confirmPendingGenerationExit,
  consumeHashRevertFlag,
  getPrimaryActiveGeneration,
  markHashRevertInProgress,
  requestGenerationExitConfirmation,
  subscribeGenerationNavigationGuard,
  isGenerationNavigationBlocked,
} from "../../lib/generationNavigationGuardStore";
import { applyHashNavigation } from "../../routes/applyHashNavigation";
import { GenerationExitConfirmDialog } from "./GenerationExitConfirmDialog";
import { useOptionalGenerationJobs } from "../../providers/GenerationJobProvider";
import { readGenerationJobsEnabled } from "../../utils/generation/generationJobConstants";

export function GenerationNavigationGuard() {
  const [state, setState] = useState(() => ({
    pendingExit: null,
    isActive: false,
  }));
  const committedHashRef = useRef(
    typeof window !== "undefined" ? window.location.hash : "",
  );
  const generationJobs = useOptionalGenerationJobs();
  const jobsEnabled = readGenerationJobsEnabled();

  useEffect(() => subscribeGenerationNavigationGuard(setState), []);

  useEffect(() => {
    if (!state.isActive) {
      committedHashRef.current = window.location.hash;
    }
  }, [state.isActive]);

  useEffect(() => {
    const onHashChange = () => {
      if (consumeHashRevertFlag()) {
        return;
      }

      if (!isGenerationNavigationBlocked()) {
        committedHashRef.current = window.location.hash;
        return;
      }

      const attemptedHash = window.location.hash;
      if (attemptedHash === committedHashRef.current) {
        return;
      }

      markHashRevertInProgress();
      window.location.hash = committedHashRef.current;

      requestGenerationExitConfirmation({
        type: "navigate",
        targetHash: attemptedHash,
        onNavigate: (targetHash) => {
          committedHashRef.current = targetHash;
          applyHashNavigation(targetHash);
        },
      });
    };

    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (!state.isActive) {
      return undefined;
    }

    function onBeforeUnload(event) {
      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [state.isActive]);

  const activeTool = getPrimaryActiveGeneration();

  async function handleLeave({ saveToLibrary = true } = {}) {
    if (
      jobsEnabled &&
      generationJobs &&
      activeTool?.jobId &&
      activeTool?.variant === "generating"
    ) {
      await generationJobs.updateSavePreference(activeTool.jobId, saveToLibrary);
    }
    confirmPendingGenerationExit();
  }

  return (
    <GenerationExitConfirmDialog
      open={Boolean(state.pendingExit)}
      toolLabel={activeTool?.label}
      variant={activeTool?.variant ?? "generating"}
      onStay={cancelPendingGenerationExit}
      onLeave={handleLeave}
    />
  );
}
